import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Plane,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  MapPin,
  AlertTriangle,
  Check,
  ChevronRight,
  ShieldCheck,
  Download,
  Edit3,
  Trash2,
  Save,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EDITABLE_STATUSES = new Set(['draft', 'submitted', 'pending_approval', 'returned_for_correction']);
const DELETABLE_STATUSES = new Set(['draft', 'submitted', 'pending_approval']);
const RECEIPT_ELIGIBLE_STATUSES = new Set(['approved', 'booked', 'completed', 'finance_approved']);

const initialFormState = {
  travel_type: 'flight',
  purpose: '',
  project: '',
  client: '',
  travel_category: 'Domestic',
  is_international: false,
  is_emergency: false,
  estimated_budget: 0,
  remarks: '',
  attachments: [],
  journey_details: [
    {
      from_city: '',
      to_city: '',
      departure_date: new Date().toISOString().split('T')[0],
      return_date: '',
      preferred_time: 'Morning (06:00 - 12:00)',
      flexible_dates: false,
    },
  ],
  booking_preferences: {
    transport_mode: 'flight',
    flight_class: 'economy',
    train_class: '3ac',
    bus_class: 'ac_sleeper',
    cab_type: 'sedan',
    hotel_rating_preferred: '4',
  },
  passengers: [
    { name: 'Self', age: 30, gender: 'Male', id_number: '', is_guest: false },
  ],
};

const ESSTravelRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // holds the req object
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editingReqId, setEditingReqId] = useState(null); // null = create, string = edit mode

  const [formStep, setFormStep] = useState(1);
  const [formData, setFormData] = useState(initialFormState);
  const [isDirty, setIsDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchMyRequests();
  }, []);

  const fetchMyRequests = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/requests`, { withCredentials: true });
      setRequests((data.requests || []).filter(r => r.status !== 'deleted'));
    } catch (err) {
      toast.error('Failed to load travel requests');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (updater) => {
    setIsDirty(true);
    if (typeof updater === 'function') {
      setFormData(updater);
    } else {
      setFormData(updater);
    }
  };

  const validateCurrentStep = () => {
    if (formStep === 1) {
      if (!formData.purpose.trim()) { toast.error('Please specify Travel Purpose.'); return false; }
      for (let i = 0; i < formData.journey_details.length; i++) {
        const leg = formData.journey_details[i];
        if (!leg.from_city.trim() || !leg.to_city.trim() || !leg.departure_date) {
          toast.error(`Please complete From City, To City, and Departure Date for Leg ${i + 1}.`);
          return false;
        }
      }
    }
    if (formStep === 2) {
      if (!formData.passengers?.length) { toast.error('Please add at least one passenger.'); return false; }
      for (let i = 0; i < formData.passengers.length; i++) {
        if (!formData.passengers[i].name?.trim()) { toast.error(`Passenger ${i + 1} name is required.`); return false; }
      }
    }
    if (formStep === 3) {
      if (!formData.estimated_budget || Number(formData.estimated_budget) <= 0) {
        toast.error('Please enter a valid Estimated Budget.');
        return false;
      }
    }
    return true;
  };

  const handleNextStep = () => { if (validateCurrentStep()) setFormStep(prev => Math.min(5, prev + 1)); };
  const handlePreviousStep = () => setFormStep(prev => Math.max(1, prev - 1));

  const handleSaveDraft = async () => {
    try {
      setSubmitting(true);
      if (editingReqId) {
        await axios.patch(`${API_URL}/travel-desk/requests/${editingReqId}`, formData, { withCredentials: true });
        toast.success('Travel Request draft updated!');
      } else {
        await axios.post(`${API_URL}/travel-desk/requests`, { ...formData, status: 'draft' }, { withCredentials: true });
        toast.success('Travel Request Draft saved!');
      }
      setIsDirty(false);
      setShowModal(false);
      setShowConfirmClose(false);
      setEditingReqId(null);
      fetchMyRequests();
    } catch (err) {
      toast.error('Failed to save draft.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (!validateCurrentStep()) return;
    try {
      setSubmitting(true);
      if (editingReqId) {
        // EDIT: PATCH existing record, then if it was draft submit it
        await axios.patch(`${API_URL}/travel-desk/requests/${editingReqId}`, { ...formData, status: 'submitted' }, { withCredentials: true });
        toast.success('Travel Request updated and submitted!');
      } else {
        await axios.post(`${API_URL}/travel-desk/requests`, { ...formData, status: 'submitted' }, { withCredentials: true });
        toast.success('Travel Request submitted successfully!');
      }
      setIsDirty(false);
      setShowModal(false);
      setEditingReqId(null);
      fetchMyRequests();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to submit travel request.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    if (isDirty) { setShowConfirmClose(true); }
    else { setShowModal(false); setEditingReqId(null); }
  };

  const handleDiscardChanges = () => {
    setShowConfirmClose(false);
    setShowModal(false);
    setIsDirty(false);
    setEditingReqId(null);
    setFormData(initialFormState);
  };

  // Open wizard in EDIT mode — pre-populate all form fields
  const handleOpenEdit = (req) => {
    const preload = {
      travel_type: req.travel_type || 'flight',
      purpose: req.purpose || '',
      project: req.project || '',
      client: req.client || '',
      travel_category: req.travel_category || 'Domestic',
      is_international: req.is_international || false,
      is_emergency: req.is_emergency || false,
      estimated_budget: req.estimated_budget || 0,
      remarks: req.remarks || '',
      attachments: req.attachments || [],
      journey_details: req.journey_details?.length
        ? req.journey_details
        : initialFormState.journey_details,
      booking_preferences: req.booking_preferences || initialFormState.booking_preferences,
      passengers: req.passengers?.length ? req.passengers : initialFormState.passengers,
    };
    setFormData(preload);
    setEditingReqId(req._id);
    setFormStep(1);
    setIsDirty(false);
    setShowModal(true);
  };

  // SOFT DELETE
  const handleConfirmDelete = async (req) => {
    try {
      await axios.delete(`${API_URL}/travel-desk/requests/${req._id}`, { withCredentials: true });
      toast.success(`Travel Request ${req.request_number} deleted.`);
      setShowDeleteConfirm(null);
      fetchMyRequests();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to delete.';
      toast.error(msg);
      setShowDeleteConfirm(null);
    }
  };

  // DOWNLOAD RECEIPT
  const handleDownloadReceipt = async (req) => {
    setDownloadingId(req._id);
    try {
      const resp = await axios.get(`${API_URL}/travel-desk/requests/${req._id}/receipt`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TravelReceipt_${req.request_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Travel Approval Receipt downloaded!');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to download receipt.';
      toast.error(msg);
    } finally {
      setDownloadingId(null);
    }
  };

  const addJourneyLeg = () => {
    updateFormData(prev => ({
      ...prev,
      journey_details: [
        ...prev.journey_details,
        { from_city: '', to_city: '', departure_date: new Date().toISOString().split('T')[0], return_date: '', preferred_time: 'Morning', flexible_dates: false },
      ],
    }));
  };

  const removeJourneyLeg = (idx) => {
    if (formData.journey_details.length <= 1) return;
    updateFormData(prev => ({ ...prev, journey_details: prev.journey_details.filter((_, i) => i !== idx) }));
  };

  const addPassenger = () => {
    updateFormData(prev => ({ ...prev, passengers: [...prev.passengers, { name: '', age: 25, gender: 'Male', id_number: '', is_guest: false }] }));
  };

  const removePassenger = (idx) => {
    if (formData.passengers.length <= 1) return;
    updateFormData(prev => ({ ...prev, passengers: prev.passengers.filter((_, i) => i !== idx) }));
  };

  const getStatusBadge = (status) => {
    const map = {
      draft: <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-300">Draft</span>,
      submitted: <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />Pending Approval</span>,
      pending_approval: <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />Pending Approval</span>,
      returned_for_correction: <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-800">Returned for Correction</span>,
      approved: <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800 flex items-center gap-1"><CheckCircle className="w-2.5 h-2.5" />Approved</span>,
      finance_approved: <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-800">Finance Approved</span>,
      booked: <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 flex items-center gap-1"><ShieldCheck className="w-2.5 h-2.5" />Ticket Booked</span>,
      rejected: <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800 flex items-center gap-1"><XCircle className="w-2.5 h-2.5" />Rejected</span>,
      cancelled: <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600">Cancelled</span>,
      completed: <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-100 text-teal-800 flex items-center gap-1"><Check className="w-2.5 h-2.5" />Completed</span>,
    };
    return map[status] || <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600 capitalize">{status}</span>;
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto font-sans">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white rounded-2xl p-6 shadow-xl flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Plane className="w-7 h-7 text-amber-400" /> My Travel Desk Requests</h1>
          <p className="text-xs text-blue-200 mt-1">Submit and track corporate travel requests, ticket bookings, and approval status.</p>
        </div>
        <button
          onClick={() => { setFormData(initialFormState); setFormStep(1); setIsDirty(false); setEditingReqId(null); setShowModal(true); }}
          className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2 text-sm transition-all"
        >
          <Plus className="w-4 h-4" /> Raise Travel Request
        </button>
      </div>

      {/* Requests List */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" /> Travel History &amp; Status</h3>
          <div className="text-xs text-slate-500 font-medium">Total: <b>{requests.length}</b></div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400 text-sm">Loading travel requests...</div>
        ) : requests.length === 0 ? (
          <div className="p-12 text-center space-y-3 text-slate-400">
            <Plane className="w-12 h-12 mx-auto text-slate-300 stroke-[1.5]" />
            <div className="text-sm font-bold text-slate-600">No Travel Requests Raised Yet</div>
            <p className="text-xs max-w-sm mx-auto">Click "Raise Travel Request" above to begin.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {requests.map((req) => (
              <div key={req._id} className="p-5 hover:bg-slate-50 transition-colors space-y-3">
                {/* Row Header */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-xs font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-md border border-blue-200">{req.request_number}</span>
                    <span className="font-bold text-slate-900 text-sm capitalize">{req.purpose} <span className="text-slate-400 font-normal">({req.travel_type})</span></span>
                    {req.is_international && <span className="text-[10px] font-bold uppercase bg-purple-100 text-purple-800 px-2 py-0.5 rounded">International</span>}
                    {req.is_emergency && <span className="text-[10px] font-bold uppercase bg-rose-100 text-rose-800 px-2 py-0.5 rounded">Emergency</span>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {getStatusBadge(req.status)}
                  </div>
                </div>

                {/* Meta info grid */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Route</div>
                    <div className="font-bold text-slate-800 mt-0.5">{req.journey_details?.[0]?.from_city || '—'} → {req.journey_details?.[0]?.to_city || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Travel Dates</div>
                    <div className="font-bold text-slate-800 mt-0.5">{req.journey_details?.[0]?.departure_date} to {req.journey_details?.[0]?.return_date || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Project / Client</div>
                    <div className="font-semibold text-slate-700 mt-0.5">{req.project || 'Internal'} / {req.client || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-400 font-bold uppercase">Est. Budget</div>
                    <div className="font-mono font-bold text-emerald-700 mt-0.5">₹ {(req.estimated_budget || 0).toLocaleString('en-IN')}</div>
                  </div>
                </div>

                {/* Actions Row */}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-slate-400 font-semibold">Step:</span>
                    <span className="capitalize font-bold text-slate-700 bg-slate-200 px-2 py-0.5 rounded text-[10px]">{req.current_approval_step?.replace(/_/g, ' ')}</span>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {/* View Timeline */}
                    <button
                      onClick={() => setSelectedRequest(req)}
                      className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-bold text-[11px] transition-all"
                    >
                      <Eye className="w-3 h-3" /> View
                    </button>

                    {/* Edit — only for editable statuses */}
                    {EDITABLE_STATUSES.has(req.status) && (
                      <button
                        onClick={() => handleOpenEdit(req)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg font-bold text-[11px] transition-all"
                      >
                        <Edit3 className="w-3 h-3" /> Edit
                      </button>
                    )}

                    {/* Delete — only for deletable statuses */}
                    {DELETABLE_STATUSES.has(req.status) && (
                      <button
                        onClick={() => setShowDeleteConfirm(req)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded-lg font-bold text-[11px] transition-all"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    )}

                    {/* Download Receipt — only after approval */}
                    {RECEIPT_ELIGIBLE_STATUSES.has(req.status) && (
                      <button
                        onClick={() => handleDownloadReceipt(req)}
                        disabled={downloadingId === req._id}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg font-bold text-[11px] transition-all disabled:opacity-60"
                      >
                        <Download className="w-3 h-3" />
                        {downloadingId === req._id ? 'Downloading...' : 'Download Receipt'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* TRAVEL REQUEST WIZARD MODAL (Create / Edit) */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden flex flex-col max-h-[92vh]">
            {/* Top bar */}
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-900 text-white flex items-center justify-between shrink-0">
              <h3 className="font-bold text-base flex items-center gap-2">
                <Plane className="w-5 h-5 text-amber-400" />
                {editingReqId ? 'Edit Travel Request' : 'Submit New Corporate Travel Request'}
              </h3>
              <button onClick={handleCloseModal} className="text-slate-400 hover:text-white font-bold text-xl leading-none">&times;</button>
            </div>

            {/* Step indicator */}
            <div className="px-4 py-2.5 bg-slate-100 border-b border-slate-200 flex items-center gap-1.5 overflow-x-auto shrink-0 text-xs">
              {[
                { step: 1, label: '1. Travel Details' },
                { step: 2, label: '2. Passengers' },
                { step: 3, label: '3. Preferences' },
                { step: 4, label: '4. Remarks' },
                { step: 5, label: '5. Review & Submit' },
              ].map((s) => (
                <div key={s.step} className="flex items-center gap-1 shrink-0">
                  <div className={`px-3 py-1 rounded-full font-bold transition-colors ${formStep === s.step ? 'bg-blue-600 text-white shadow-sm' : formStep > s.step ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'}`}>
                    {s.label}
                  </div>
                  {s.step < 5 && <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                </div>
              ))}
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto space-y-5 text-xs flex-1">

              {/* STEP 1: TRAVEL DETAILS */}
              {formStep === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Travel Mode *</label>
                      <select value={formData.travel_type} onChange={e => updateFormData({ ...formData, travel_type: e.target.value })} className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium outline-none">
                        <option value="flight">Flight Ticket</option>
                        <option value="train">Train Ticket</option>
                        <option value="bus">Bus Ticket</option>
                        <option value="cab">Cab Booking</option>
                        <option value="hotel">Hotel Accommodation</option>
                        <option value="multi_city">Multi-city Travel</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Travel Category</label>
                      <select value={formData.travel_category} onChange={e => updateFormData({ ...formData, travel_category: e.target.value, is_international: e.target.value === 'International' })} className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium outline-none">
                        <option value="Domestic">Domestic</option>
                        <option value="International">International</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Travel Purpose *</label>
                      <input type="text" value={formData.purpose} onChange={e => updateFormData({ ...formData, purpose: e.target.value })} className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium outline-none" placeholder="e.g. Client Architecture Workshop" required />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Project Name</label>
                      <input type="text" value={formData.project} onChange={e => updateFormData({ ...formData, project: e.target.value })} className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium outline-none" />
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Client Name</label>
                      <input type="text" value={formData.client} onChange={e => updateFormData({ ...formData, client: e.target.value })} className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium outline-none" />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-bold text-slate-800 uppercase tracking-wider text-[11px]">Journey Legs &amp; Route *</h4>
                      <button type="button" onClick={addJourneyLeg} className="text-xs font-bold text-blue-600 hover:underline">+ Add City / Leg</button>
                    </div>
                    {formData.journey_details.map((leg, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-slate-700 text-[10px]">LEG {idx + 1}</span>
                          {formData.journey_details.length > 1 && (
                            <button type="button" onClick={() => removeJourneyLeg(idx)} className="text-rose-600 font-bold hover:underline text-[10px] flex items-center gap-0.5"><Trash2 className="w-3 h-3" />Remove</button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {['from_city', 'to_city', 'departure_date', 'return_date'].map((field) => (
                            <div key={field}>
                              <label className="block text-[10px] font-bold text-slate-500 capitalize">{field.replace('_', ' ')}{field === 'from_city' || field === 'to_city' || field === 'departure_date' ? ' *' : ''}</label>
                              <input
                                type={field.includes('date') ? 'date' : 'text'}
                                value={leg[field] || ''}
                                onChange={e => {
                                  const nl = [...formData.journey_details];
                                  nl[idx][field] = e.target.value;
                                  updateFormData({ ...formData, journey_details: nl });
                                }}
                                className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs"
                                required={['from_city', 'to_city', 'departure_date'].includes(field)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 2: PASSENGERS */}
              {formStep === 2 && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-bold text-slate-800 uppercase tracking-wider text-[11px]">Passenger Manifest *</h4>
                    <button type="button" onClick={addPassenger} className="text-xs font-bold text-blue-600 hover:underline">+ Add Passenger</button>
                  </div>
                  {formData.passengers.map((p, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-slate-700 text-[10px]">PASSENGER {idx + 1}</span>
                        {formData.passengers.length > 1 && (
                          <button type="button" onClick={() => removePassenger(idx)} className="text-rose-600 font-bold hover:underline text-[10px] flex items-center gap-0.5"><Trash2 className="w-3 h-3" />Remove</button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500">Full Name *</label>
                          <input type="text" value={p.name} onChange={e => { const np=[...formData.passengers]; np[idx].name=e.target.value; updateFormData({...formData,passengers:np}); }} className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs" required />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500">Age</label>
                          <input type="number" value={p.age} onChange={e => { const np=[...formData.passengers]; np[idx].age=Number(e.target.value); updateFormData({...formData,passengers:np}); }} className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500">Gender</label>
                          <select value={p.gender} onChange={e => { const np=[...formData.passengers]; np[idx].gender=e.target.value; updateFormData({...formData,passengers:np}); }} className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs">
                            <option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500">ID / Passport No</label>
                          <input type="text" value={p.id_number} onChange={e => { const np=[...formData.passengers]; np[idx].id_number=e.target.value; updateFormData({...formData,passengers:np}); }} className="w-full h-8 px-2 bg-white border border-slate-300 rounded-lg text-xs" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* STEP 3: PREFERENCES */}
              {formStep === 3 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                      { label: 'Flight Class', key: 'flight_class', options: [{v:'economy',l:'Economy'},{v:'premium_economy',l:'Premium Economy'},{v:'business',l:'Business Class'}] },
                      { label: 'Train Class', key: 'train_class', options: [{v:'3ac',l:'3AC'},{v:'2ac',l:'2AC'},{v:'1ac',l:'1AC'},{v:'sleeper',l:'Sleeper'}] },
                      { label: 'Cab Type', key: 'cab_type', options: [{v:'sedan',l:'Sedan'},{v:'suv',l:'SUV'},{v:'hatchback',l:'Hatchback'},{v:'luxury',l:'Luxury'}] },
                    ].map(f => (
                      <div key={f.key}>
                        <label className="block font-bold text-slate-700 mb-1">{f.label}</label>
                        <select value={formData.booking_preferences[f.key]} onChange={e => updateFormData({...formData, booking_preferences:{...formData.booking_preferences,[f.key]:e.target.value}})} className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium outline-none">
                          {f.options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Hotel Star Rating</label>
                      <select value={formData.booking_preferences.hotel_rating_preferred} onChange={e => updateFormData({...formData, booking_preferences:{...formData.booking_preferences, hotel_rating_preferred:e.target.value}})} className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-medium outline-none">
                        <option value="3">3 Star</option><option value="4">4 Star</option><option value="5">5 Star</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Estimated Trip Budget (₹) *</label>
                      <input type="number" value={formData.estimated_budget} onChange={e => updateFormData({...formData, estimated_budget:Number(e.target.value)})} className="w-full h-9 px-2.5 bg-slate-50 border border-slate-300 rounded-xl font-mono font-bold outline-none" required />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 4: REMARKS */}
              {formStep === 4 && (
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Special Instructions &amp; Remarks</label>
                  <textarea rows={4} value={formData.remarks} onChange={e => updateFormData({...formData, remarks:e.target.value})} placeholder="e.g. Prefer morning flight, aisle seat preference, non-smoking hotel..." className="w-full p-2.5 bg-slate-50 border border-slate-300 rounded-xl outline-none" />
                </div>
              )}

              {/* STEP 5: REVIEW */}
              {formStep === 5 && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-xl text-blue-900 text-xs flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 shrink-0" />
                    <span>Review all details below before final {editingReqId ? 'update' : 'submission'}.</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <div><b>Travel Mode:</b> <span className="capitalize">{formData.travel_type.replace('_', ' ')}</span></div>
                    <div><b>Category:</b> {formData.travel_category}</div>
                    <div><b>Purpose:</b> {formData.purpose}</div>
                    <div><b>Project / Client:</b> {formData.project} / {formData.client}</div>
                    <div><b>Est. Budget:</b> <span className="font-mono font-bold text-emerald-700">₹ {Number(formData.estimated_budget).toLocaleString('en-IN')}</span></div>
                    <div><b>Passengers:</b> {formData.passengers.length} Person(s)</div>
                  </div>
                  <div className="space-y-1">
                    <h5 className="font-bold text-slate-800 uppercase text-[10px]">Journey Route</h5>
                    {formData.journey_details.map((leg, idx) => (
                      <div key={idx} className="p-2 bg-white rounded-lg border border-slate-200 flex justify-between text-xs">
                        <span><b>Leg {idx + 1}:</b> {leg.from_city} → {leg.to_city}</span>
                        <span><b>Departure:</b> {leg.departure_date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Action Bar */}
            <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex justify-between items-center shrink-0">
              {formStep > 1 ? (
                <button type="button" onClick={handlePreviousStep} className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl font-bold transition-all">← Previous</button>
              ) : <div />}

              <div className="flex gap-2.5 items-center">
                <button type="button" onClick={handleSaveDraft} disabled={submitting} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold flex items-center gap-1.5 transition-all shadow-sm disabled:opacity-50">
                  <Save className="w-3.5 h-3.5 text-amber-400" /> Save Draft
                </button>
                {formStep < 5 ? (
                  <button type="button" onClick={handleNextStep} className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold flex items-center gap-1.5 shadow-md transition-all">
                    Next Step →
                  </button>
                ) : (
                  <button type="button" onClick={handleFinalSubmit} disabled={submitting} className="px-6 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-xl font-extrabold flex items-center gap-1.5 shadow-lg transition-all disabled:opacity-50">
                    <Check className="w-4 h-4" /> {editingReqId ? 'Update & Submit Request' : 'Submit Travel Request'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* UNSAVED CHANGES CONFIRMATION */}
      {showConfirmClose && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3 text-amber-600"><AlertTriangle className="w-6 h-6 shrink-0" /><h3 className="font-bold text-slate-900 text-base">Unsaved Changes</h3></div>
            <p className="text-xs text-slate-600 leading-relaxed">You have unsaved changes. Do you want to Save Draft before leaving?</p>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 text-xs font-bold">
              <button onClick={() => setShowConfirmClose(false)} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl">Cancel</button>
              <button onClick={handleDiscardChanges} className="px-3 py-2 bg-rose-100 hover:bg-rose-200 text-rose-800 rounded-xl">Discard</button>
              <button onClick={handleSaveDraft} className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-xl shadow-md">Save Draft</button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3 text-rose-600"><Trash2 className="w-6 h-6 shrink-0" /><h3 className="font-bold text-slate-900 text-base">Delete Travel Request?</h3></div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Are you sure you want to delete Travel Request <b>{showDeleteConfirm.request_number}</b>?<br/>
              The record will be soft-deleted and retained in audit logs.
            </p>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 text-xs font-bold">
              <button onClick={() => setShowDeleteConfirm(null)} className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl">Cancel</button>
              <button onClick={() => handleConfirmDelete(showDeleteConfirm)} className="px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl shadow-md">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW DETAIL / TIMELINE MODAL */}
      {selectedRequest && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Request Details &amp; Approval Timeline</h3>
              <button onClick={() => setSelectedRequest(null)} className="text-slate-400 hover:text-slate-600 font-bold text-xl">&times;</button>
            </div>
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl">
                <div><b>Request No:</b> {selectedRequest.request_number}</div>
                <div><b>Purpose:</b> {selectedRequest.purpose}</div>
                <div><b>Est. Budget:</b> ₹ {(selectedRequest.estimated_budget || 0).toLocaleString('en-IN')}</div>
                <div><b>Status:</b> {selectedRequest.status}</div>
              </div>
              <div>
                <h4 className="font-bold text-slate-800 mb-2 uppercase text-[10px]">Approval Audit Timeline</h4>
                <div className="space-y-2 border-l-2 border-blue-500 pl-3">
                  {(selectedRequest.approval_timeline || []).map((t, idx) => (
                    <div key={idx} className="space-y-0.5">
                      <div className="font-bold text-slate-900">{t.actor_name} <span className="font-normal text-slate-500">({t.step})</span></div>
                      <div className="text-slate-500 text-[10px]">{t.timestamp?.slice(0, 19)?.replace('T', ' ')} — <span className="capitalize font-semibold text-blue-600">{t.action}</span></div>
                      {t.comments && <div className="text-slate-600 bg-slate-100 p-1.5 rounded">{t.comments}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ESSTravelRequests;
